import { debug } from './debug.js';
import type { FacingMode, DeviceId, Camera } from './types.js';

export class CameraPermissionError extends Error {
  constructor(
    message = 'Camera access denied. Please grant camera permission and try again.',
  ) {
    super(message);
    this.name = 'CameraPermissionError';
  }
}

export class CameraNotFoundError extends Error {
  constructor(
    message = 'No camera found. Please connect a camera and try again.',
  ) {
    super(message);
    this.name = 'CameraNotFoundError';
  }
}

const CACHE_KEY_PREFIX = '@agicash/qr-scanner:camera:';

function getCachedDeviceId(facingMode: string): string | null {
  try {
    return localStorage.getItem(`${CACHE_KEY_PREFIX}${facingMode}`);
  } catch {
    return null;
  }
}

function setCachedDeviceId(facingMode: string, deviceId: string): void {
  try {
    localStorage.setItem(`${CACHE_KEY_PREFIX}${facingMode}`, deviceId);
  } catch {
    // localStorage unavailable or full — ignore
  }
}

function clearCachedDeviceId(facingMode: string): void {
  try {
    localStorage.removeItem(`${CACHE_KEY_PREFIX}${facingMode}`);
  } catch {
    // localStorage unavailable — ignore
  }
}

export interface CameraConfig {
  preferredCamera?: FacingMode | DeviceId;
  cameraResolution?: {
    width?: MediaTrackConstraintSet['width'];
    height?: MediaTrackConstraintSet['height'];
  };
}

export class CameraManager {
  private stream: MediaStream | null = null;
  private facingMode: FacingMode | DeviceId;
  private resolution: CameraConfig['cameraResolution'];

  constructor(config: CameraConfig = {}) {
    this.facingMode = config.preferredCamera ?? 'environment';
    this.resolution = config.cameraResolution;
  }

  async start(video: HTMLVideoElement): Promise<MediaStream> {
    if (this.stream) {
      return this.stream;
    }

    const t0 = performance.now();

    this.stream = await this.acquireStream();
    const t1 = performance.now();
    debug(`[QrScanner] acquireStream: ${(t1 - t0).toFixed(0)}ms`);

    // On some devices (e.g. Samsung S24 + Brave), facingMode: 'environment'
    // picks an ultrawide camera that lacks autofocus. Check and switch to a
    // better camera BEFORE showing on screen to avoid visible flicker.
    await this.ensureBestCamera();
    const t2 = performance.now();
    debug(`[QrScanner] ensureBestCamera: ${(t2 - t1).toFixed(0)}ms`);

    video.srcObject = this.stream;
    video.setAttribute('playsinline', 'true');
    await video.play();
    const t3 = performance.now();
    debug(`[QrScanner] video.play: ${(t3 - t2).toFixed(0)}ms`);
    debug(`[QrScanner] camera.start total: ${(t3 - t0).toFixed(0)}ms`);

    // Cache the final camera so subsequent starts skip ensureBestCamera
    if (this.facingMode === 'environment' || this.facingMode === 'user') {
      const finalDeviceId = this.getVideoTrack()?.getSettings().deviceId;
      if (finalDeviceId) {
        setCachedDeviceId(this.facingMode, finalDeviceId);
      }
    }

    return this.stream;
  }

  stop(): void {
    if (this.stream) {
      for (const track of this.stream.getTracks()) {
        track.stop();
      }
      this.stream = null;
    }
  }

  async setCamera(
    facingModeOrDeviceId: FacingMode | DeviceId,
    video: HTMLVideoElement,
  ): Promise<void> {
    this.stop();
    this.facingMode = facingModeOrDeviceId;
    await this.start(video);
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  async hasFlash(): Promise<boolean> {
    const track = this.getVideoTrack();
    if (!track) return false;

    try {
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
        torch?: boolean;
      };
      return capabilities.torch === true;
    } catch {
      return false;
    }
  }

  isFlashOn(): boolean {
    const track = this.getVideoTrack();
    if (!track) return false;

    const settings = track.getSettings() as MediaTrackSettings & {
      torch?: boolean;
    };
    return settings.torch === true;
  }

  async toggleFlash(): Promise<void> {
    if (this.isFlashOn()) {
      await this.turnFlashOff();
    } else {
      await this.turnFlashOn();
    }
  }

  async turnFlashOn(): Promise<void> {
    await this.setTorch(true);
  }

  async turnFlashOff(): Promise<void> {
    await this.setTorch(false);
  }

  private async setTorch(on: boolean): Promise<void> {
    const track = this.getVideoTrack();
    if (!track) {
      throw new Error('No active camera stream');
    }

    try {
      await track.applyConstraints({
        advanced: [{ torch: on } as MediaTrackConstraintSet],
      });
    } catch {
      throw new Error('Flash/torch is not supported on this device');
    }
  }

  /**
   * If the current camera lacks continuous autofocus (e.g. an ultrawide sensor
   * picked by facingMode: 'environment'), find a better camera with the same
   * facing mode and replace this.stream. Called before assigning to the video
   * element so the user never sees the wrong camera.
   */
  private async ensureBestCamera(): Promise<void> {
    if (this.facingMode !== 'environment' && this.facingMode !== 'user') {
      debug('[QrScanner] ensureBestCamera: skipped (specific deviceId)');
      return;
    }

    const track = this.getVideoTrack();
    if (!track) return;

    try {
      const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
        focusMode?: string[];
      };
      if (
        !capabilities.focusMode ||
        capabilities.focusMode.includes('continuous')
      ) {
        // focusMode not reported (e.g. Safari/iOS) or has autofocus — skip.
        // Only enter the candidate loop when the browser explicitly reports
        // focusMode without 'continuous' (e.g. S24 + Brave ultrawide).
        debug(
          `[QrScanner] ensureBestCamera: skipped (focusMode: ${JSON.stringify(capabilities.focusMode)})`,
        );
        return;
      }
      debug(
        `[QrScanner] ensureBestCamera: current camera lacks autofocus (focusMode: ${JSON.stringify(capabilities.focusMode)})`,
      );
    } catch (err) {
      debug(
        `[QrScanner] ensureBestCamera: skipped (getCapabilities failed: ${err instanceof Error ? err.message : err})`,
      );
      return;
    }

    // Current camera lacks continuous autofocus.
    // Enumerate devices while stream is active (ensures deviceIds are available).
    const currentDeviceId = track.getSettings().deviceId;

    let devices: MediaDeviceInfo[];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch (err) {
      debug(
        `[QrScanner] ensureBestCamera: skipped (enumerateDevices failed: ${err instanceof Error ? err.message : err})`,
      );
      return;
    }
    if (!Array.isArray(devices)) return;

    const candidates = devices.filter(
      (d) => d.kind === 'videoinput' && d.deviceId !== currentDeviceId,
    );
    debug(
      `[QrScanner] ensureBestCamera: testing ${candidates.length} candidate camera(s)`,
    );
    if (candidates.length === 0) return;

    // Stop current stream — mobile devices only allow one active camera
    this.stop();

    for (const candidate of candidates) {
      const t = performance.now();
      let candidateStream: MediaStream;
      try {
        candidateStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: candidate.deviceId },
            width: this.resolution?.width ?? { ideal: 1920 },
            height: this.resolution?.height ?? { ideal: 1080 },
          },
          audio: false,
        });
        debug(
          `[QrScanner] ensureBestCamera: candidate ${candidate.label || candidate.deviceId.slice(0, 8)}: getUserMedia ${(performance.now() - t).toFixed(0)}ms`,
        );
      } catch (err) {
        debug(
          `[QrScanner] ensureBestCamera: candidate ${candidate.label || candidate.deviceId.slice(0, 8)}: getUserMedia failed ${(performance.now() - t).toFixed(0)}ms — ${err instanceof Error ? err.name : err}`,
        );
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          throw new CameraPermissionError();
        }
        continue;
      }

      const candidateTrack = candidateStream.getVideoTracks()[0];
      if (!candidateTrack) {
        for (const t of candidateStream.getTracks()) t.stop();
        continue;
      }

      // Must match the desired facing mode
      const candidateSettings =
        candidateTrack.getSettings() as MediaTrackSettings & {
          facingMode?: string;
        };
      if (
        candidateSettings.facingMode &&
        candidateSettings.facingMode !== this.facingMode
      ) {
        for (const t of candidateStream.getTracks()) t.stop();
        continue;
      }

      // Check if this camera supports continuous autofocus
      try {
        const candidateCaps =
          candidateTrack.getCapabilities() as MediaTrackCapabilities & {
            focusMode?: string[];
          };
        if (candidateCaps.focusMode?.includes('continuous')) {
          this.stream = candidateStream;
          return;
        }
      } catch (err) {
        debug(
          `[QrScanner] ensureBestCamera: candidate getCapabilities failed — ${err instanceof Error ? err.message : err}`,
        );
      }

      for (const t of candidateStream.getTracks()) t.stop();
    }

    // No better camera found — re-open the original with progressively
    // simpler constraints so we never leave this.stream as null.
    const recoveryAttempts: MediaStreamConstraints[] = [
      // Original deviceId + resolution
      {
        video: {
          deviceId: currentDeviceId ? { exact: currentDeviceId } : undefined,
          width: this.resolution?.width ?? { ideal: 1920 },
          height: this.resolution?.height ?? { ideal: 1080 },
        },
        audio: false,
      },
      // facingMode + resolution
      this.buildConstraints(),
      // facingMode only
      this.buildConstraints(false),
      // Bare minimum — should always succeed if a camera exists
      { video: true, audio: false },
    ];

    let lastRecoveryError: unknown;
    for (const constraints of recoveryAttempts) {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia(constraints);
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          throw new CameraPermissionError();
        }
        if (err instanceof DOMException && err.name === 'NotFoundError') {
          throw new CameraNotFoundError();
        }
        lastRecoveryError = err;
        debug(
          `[QrScanner] ensureBestCamera recovery failed: ${err instanceof Error ? err.name : err}`,
        );
        continue;
      }
    }
    debug(
      `[QrScanner] ensureBestCamera: all recovery attempts failed, last error: ${lastRecoveryError instanceof Error ? lastRecoveryError.message : lastRecoveryError}`,
    );
  }

  private getVideoTrack(): MediaStreamTrack | null {
    if (!this.stream) return null;
    const tracks = this.stream.getVideoTracks();
    return tracks[0] ?? null;
  }

  /**
   * Try getUserMedia with progressively simpler constraints.
   *
   * Some browsers (e.g. Brave on Samsung Galaxy S24) throw NotReadableError
   * when facingMode and resolution constraints are combined. Falling back to
   * fewer constraints lets us still open the camera on those browsers.
   */
  private async acquireStream(): Promise<MediaStream> {
    const attempts: {
      label: string;
      constraints: MediaStreamConstraints;
      isCachedDeviceId?: boolean;
    }[] = [];

    // If we've previously found a good camera for this facingMode, try it first
    if (this.facingMode === 'environment' || this.facingMode === 'user') {
      const cachedId = getCachedDeviceId(this.facingMode);
      if (cachedId) {
        const video: MediaTrackConstraints = {
          deviceId: { exact: cachedId },
        };
        if (this.resolution?.width) video.width = this.resolution.width;
        else video.width = { ideal: 1920 };
        if (this.resolution?.height) video.height = this.resolution.height;
        else video.height = { ideal: 1080 };
        attempts.push({
          label: 'cached deviceId',
          constraints: { video, audio: false },
          isCachedDeviceId: true,
        });
      }
    }

    // Standard fallback chain
    attempts.push(
      { label: 'full constraints', constraints: this.buildConstraints() },
      { label: 'no resolution', constraints: this.buildConstraints(false) },
      { label: 'bare minimum', constraints: { video: true, audio: false } },
    );

    let lastError: unknown;
    for (const attempt of attempts) {
      const t = performance.now();
      try {
        const stream = await navigator.mediaDevices.getUserMedia(
          attempt.constraints,
        );
        debug(
          `[QrScanner] getUserMedia(${attempt.label}): ${(performance.now() - t).toFixed(0)}ms ✓`,
        );
        return stream;
      } catch (err) {
        debug(
          `[QrScanner] getUserMedia(${attempt.label}): ${(performance.now() - t).toFixed(0)}ms ✗ ${err instanceof Error ? err.name : err}`,
        );
        // Permission denied and no camera are fatal — no fallback can help.
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          throw new CameraPermissionError();
        }
        if (err instanceof DOMException && err.name === 'NotFoundError') {
          throw new CameraNotFoundError();
        }
        // DOMException subtypes (NotReadableError, etc.) and
        // OverconstrainedError are retryable with simpler constraints.
        if (
          !(err instanceof DOMException) &&
          !(err instanceof OverconstrainedError)
        ) {
          throw err;
        }
        if (attempt.isCachedDeviceId) {
          // Stale cached deviceId (e.g. iOS regenerated device IDs after an
          // update). Clear it so subsequent attempts don't repeat the failure.
          clearCachedDeviceId(this.facingMode);
          debug(
            `[QrScanner] cleared stale cached deviceId for "${this.facingMode}"`,
          );
        }
        lastError = err;
        continue;
      }
    }

    throw lastError;
  }

  private buildConstraints(includeResolution = true): MediaStreamConstraints {
    const video: MediaTrackConstraints = {};

    if (includeResolution) {
      video.width = this.resolution?.width ?? { ideal: 1920 };
      video.height = this.resolution?.height ?? { ideal: 1080 };
    }

    if (this.facingMode === 'environment' || this.facingMode === 'user') {
      video.facingMode = this.facingMode;
    } else {
      video.deviceId = { exact: this.facingMode };
    }

    return { video, audio: false };
  }

  static async hasCamera(): Promise<boolean> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.some((d) => d.kind === 'videoinput');
    } catch {
      return false;
    }
  }

  static async listCameras(requestLabels = false): Promise<Camera[]> {
    if (requestLabels) {
      // Requesting labels requires a temporary stream to trigger the permission prompt
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        for (const track of stream.getTracks()) {
          track.stop();
        }
      } catch {
        // Permission denied — fall through with empty labels
      }
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((d) => d.kind === 'videoinput')
      .map((d) => ({
        id: d.deviceId,
        label: d.label || `Camera ${d.deviceId.slice(0, 8)}`,
      }));
  }
}
